import React, { useState } from 'react';
import {
  siCloudflare, siLetsencrypt, siFastly, siVercel, siNetlify,
  siGithub, siRender, siFlydotio, siRailway, siDigitalocean,
  siMailgun, siGodaddy, siNamecheap, siAkamai,
} from 'simple-icons';
import { ExternalLink, RefreshCw } from 'lucide-react';
import SEO from '@/components/SEO';
import { Pill } from '@/components/flat';
import { timeAgo, RegistrarStatus, ServiceStatus } from '@/hooks/useRadarData';
import { useRadarRegistrars, useRadarServices } from '@/hooks/useRadarQueries';

// ── Logos ──────────────────────────────────────────────────────────────────────

type SI = { path: string; hex: string; title: string };

const SIMPLE_ICON: Record<string, SI> = {
  cloudflare: siCloudflare, letsencrypt: siLetsencrypt, fastly: siFastly,
  vercel: siVercel, netlify: siNetlify, github: siGithub, render: siRender,
  flyio: siFlydotio, railway: siRailway, digitalocean: siDigitalocean,
  linode: siAkamai, mailgun: siMailgun, godaddy: siGodaddy, namecheap: siNamecheap,
};

const CLEARBIT_DOMAIN: Record<string, string> = {
  ns1: 'ns1.com', dnsimple: 'dnsimple.com', digicert: 'digicert.com',
  heroku: 'heroku.com', sendgrid: 'sendgrid.com', postmark: 'postmarkapp.com',
  'name.com': 'name.com', porkbun: 'porkbun.com', dynadot: 'dynadot.com',
  hover: 'hover.com', google: 'domains.google', 'network-solutions': 'networksolutions.com',
};

function ServiceLogo({ id, name }: { id: string; name: string }) {
  const [clearbitFailed, setClearbitFailed] = useState(false);
  const si = SIMPLE_ICON[id];
  if (si) {
    return (
      <span className="w-6 h-6 rounded flex-shrink-0 flex items-center justify-center bg-white border border-gray-100">
        <svg role="img" viewBox="0 0 24 24" className="w-3.5 h-3.5" aria-label={name}>
          <path d={si.path} fill={`#${si.hex}`} />
        </svg>
      </span>
    );
  }
  const domain = CLEARBIT_DOMAIN[id];
  if (domain && !clearbitFailed) {
    return (
      <img src={`https://logo.clearbit.com/${domain}`} alt={name} width={24} height={24}
        className="w-6 h-6 rounded object-contain flex-shrink-0 border border-gray-100"
        onError={() => setClearbitFailed(true)} />
    );
  }
  return (
    <span className="w-6 h-6 rounded flex items-center justify-center bg-gray-100 text-gray-500 text-[10px] font-bold flex-shrink-0 select-none">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

// ── Sparkline ─────────────────────────────────────────────────────────────────

const STATUS_COLOR = { operational: '#10B981', degraded: '#F59E0B', unknown: '#D1D5DB' };

function ActivityBars({ data, status }: { data: number[]; status: RegistrarStatus['status'] }) {
  const max = Math.max(...data, 1);
  const color = STATUS_COLOR[status];
  const barW = 5; const gap = 2; const h = 36;
  const totalW = data.length * (barW + gap) - gap;
  return (
    <svg width={totalW} height={h} viewBox={`0 0 ${totalW} ${h}`} aria-hidden="true">
      {data.map((v, i) => {
        const barH = v > 0 ? Math.max(Math.round((v / max) * h), 3) : 0;
        return <rect key={i} x={i * (barW + gap)} y={h - barH} width={barW} height={barH || 2}
          rx={1.5} fill={color} opacity={v === 0 ? 0.18 : 0.9} />;
      })}
    </svg>
  );
}

function RegistrarMiniChart({ r }: { r: RegistrarStatus }) {
  const hasActivity = r.hourlyActivity.some(v => v > 0);
  if (!hasActivity) return <span className="text-xs text-gray-400">No scans yet</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <ActivityBars data={r.hourlyActivity} status={r.status} />
      {r.recordsLast24h > 0 && <span className="text-xs text-gray-400 whitespace-nowrap">{r.recordsLast24h} today</span>}
    </span>
  );
}

// ── Status dot ────────────────────────────────────────────────────────────────

type DotStatus = 'operational' | 'degraded' | 'outage' | 'unknown';

function StatusDot({ status }: { status: DotStatus }) {
  const base = { operational: 'bg-emerald-500', degraded: 'bg-amber-400', outage: 'bg-red-500', unknown: 'bg-gray-300' };
  const ping = { operational: 'bg-emerald-400', degraded: 'bg-amber-300', outage: 'bg-red-400', unknown: 'bg-gray-200' };
  return (
    <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
      {status !== 'unknown' && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${ping[status]}`} />}
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${base[status]}`} />
    </span>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────

type TableRow =
  | { kind: 'registrar'; data: RegistrarStatus }
  | { kind: 'service';   data: ServiceStatus }
  | { kind: 'ref';       label: string; registrar: string };

const REG_STATUS_LABEL: Record<RegistrarStatus['status'], string> = { operational: 'Operational', degraded: 'Degraded', unknown: 'No data' };
const REG_STATUS_PILL: Record<RegistrarStatus['status'], 'success' | 'warning' | 'muted'> = { operational: 'success', degraded: 'warning', unknown: 'muted' };
const SVC_STATUS_LABEL: Record<ServiceStatus['status'], string> = { operational: 'Operational', degraded: 'Degraded', outage: 'Outage', unknown: 'Unknown' };
const SVC_STATUS_PILL: Record<ServiceStatus['status'], 'success' | 'warning' | 'error' | 'muted'> = { operational: 'success', degraded: 'warning', outage: 'error', unknown: 'muted' };
const CATEGORY_LABEL: Record<ServiceStatus['category'], string> = {
  dns: 'DNS', ssl: 'SSL', hosting: 'Hosting', cdn: 'CDN', email: 'Email', cloud: 'Cloud',
};

function StatusTable({ rows, loading }: { rows: TableRow[]; loading: boolean }) {
  return (
    <div className="border border-gray-100 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className="w-6 px-4 py-2.5" />
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Service</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Type</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden lg:table-cell">Activity / Details</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Checked</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {loading
            ? Array.from({ length: 9 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-4 py-3"><div className="w-2.5 h-2.5 rounded-full bg-gray-200 animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-3.5 w-28 bg-gray-200 rounded animate-pulse" /></td>
                  <td className="px-4 py-3 hidden sm:table-cell"><div className="h-4 w-16 bg-gray-100 rounded animate-pulse" /></td>
                  <td className="px-4 py-3"><div className="h-4 w-20 bg-gray-100 rounded animate-pulse" /></td>
                  <td className="px-4 py-3 hidden lg:table-cell"><div className="h-3 w-36 bg-gray-100 rounded animate-pulse" /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><div className="h-3 w-10 bg-gray-100 rounded animate-pulse ml-auto" /></td>
                  <td />
                </tr>
              ))
            : rows.map((row, idx) => {
                if (row.kind === 'registrar') {
                  const r = row.data;
                  return (
                    <tr key={r.registrar} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3"><StatusDot status={r.status} /></td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2">
                          <ServiceLogo id={r.registrar} name={r.label} />
                          <span className="font-semibold text-gray-900">{r.label}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell"><Pill variant="info">Registrar</Pill></td>
                      <td className="px-4 py-3"><Pill variant={REG_STATUS_PILL[r.status]}>{REG_STATUS_LABEL[r.status]}</Pill></td>
                      <td className="px-4 py-3 hidden lg:table-cell"><RegistrarMiniChart r={r} /></td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-400 text-right whitespace-nowrap">
                        {r.lastRecordAt ? timeAgo(r.lastRecordAt) : '—'}
                      </td>
                      <td className="px-4 py-2" />
                    </tr>
                  );
                }
                if (row.kind === 'service') {
                  const s = row.data;
                  return (
                    <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3"><StatusDot status={s.status} /></td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2">
                          <ServiceLogo id={s.id} name={s.name} />
                          <span className="font-semibold text-gray-900">{s.name}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell"><Pill variant="muted">{CATEGORY_LABEL[s.category]}</Pill></td>
                      <td className="px-4 py-3"><Pill variant={SVC_STATUS_PILL[s.status]}>{SVC_STATUS_LABEL[s.status]}</Pill></td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-400 max-w-xs truncate">{s.description || '—'}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-400 text-right whitespace-nowrap">{timeAgo(s.lastCheckedAt)}</td>
                      <td className="px-4 py-2 text-right">
                        <a href={s.statusPageUrl} target="_blank" rel="noopener noreferrer"
                          className="text-gray-300 hover:text-blue-500 transition-colors inline-block" aria-label={`${s.name} status page`}>
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={`ref-${idx}`} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3"><StatusDot status="unknown" /></td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <ServiceLogo id={row.registrar} name={row.label} />
                        <span className="font-medium text-gray-500">{row.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell"><Pill variant="muted">Registrar</Pill></td>
                    <td className="px-4 py-3"><Pill variant="muted">Not monitored</Pill></td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-gray-400">—</td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-400 text-right">—</td>
                    <td className="px-4 py-2" />
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const RadarStatus: React.FC = () => {
  const { data: { registrars = [] } = {}, isLoading: registrarsLoading } = useRadarRegistrars();
  const { data: { services = [] } = {}, isLoading: servicesLoading } = useRadarServices();

  const tracked   = registrars.filter(r => r.tracked);
  const reference = registrars.filter(r => !r.tracked);

  const tableRows: TableRow[] = [
    ...tracked.map(r => ({ kind: 'registrar' as const, data: r })),
    ...services.map(s => ({ kind: 'service' as const, data: s })),
    ...reference.map(r => ({ kind: 'ref' as const, label: r.label, registrar: r.registrar })),
  ];
  const tableLoading = registrarsLoading || servicesLoading;

  const issueCount    = tracked.filter(r => r.status === 'degraded').length;
  const serviceIssues = services.filter(s => s.status === 'outage' || s.status === 'degraded').length;
  const operationalTracked = tracked.filter(r => r.status === 'operational').length;
  const aggregateStatus = issueCount > 0 || serviceIssues > 0 ? 'issues' : tableLoading ? 'loading' : 'ok';

  return (
    <>
      <SEO
        title="Registrar & Service Status | DomBase Radar"
        description="Live status monitoring for domain registrars and infrastructure services. DNS, SSL, CDN, hosting and email providers."
        canonical="/radar/status"
      />

      {/* Page header */}
      <div className="border-b border-gray-100 bg-white px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-5xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Registrar &amp; Service Status</h1>
            <p className="text-sm text-gray-400 mt-0.5">Live infrastructure monitoring across DNS, SSL, CDN, hosting and email</p>
          </div>
          {!tableLoading && (
            <div className={`flex-shrink-0 rounded-md px-3 py-2 border ${
              aggregateStatus === 'ok'     ? 'bg-gray-50 border-gray-200' :
              aggregateStatus === 'issues' ? 'bg-amber-50 border-amber-200' :
                                             'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center gap-2 mb-0.5">
                <StatusDot status={aggregateStatus === 'ok' ? 'operational' : aggregateStatus === 'issues' ? 'degraded' : 'unknown'} />
                <span className="text-sm font-semibold text-gray-900">
                  {aggregateStatus === 'ok' ? 'All systems normal' : aggregateStatus === 'issues' ? 'Some issues detected' : 'Monitoring…'}
                </span>
              </div>
              <p className="text-xs text-gray-400">
                {operationalTracked} of {tracked.length} registrars operational
                {serviceIssues > 0 && ` · ${serviceIssues} service${serviceIssues > 1 ? 's' : ''} affected`}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Registrars &amp; Services
          </h2>
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <RefreshCw className="h-3 w-3" />Registrars every 60s · Services every 5 min
          </span>
        </div>
        <StatusTable rows={tableRows} loading={tableLoading} />
      </div>
    </>
  );
};

export default RadarStatus;
